/**
 * The exit country picker, for the URL console and the file submit page.
 *
 * Untouched, it shows the deployment default and sends nothing, so intake
 * applies whatever the default is when the run is accepted rather than what it
 * was when the page loaded.
 */

import { useMemo } from 'react';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { Select, type SelectOption } from '@/components/ui/Select';
import { countryName } from '@/lib/countries';

/**
 * The analyst's pick while the current offer still has it, else null.
 *
 * A pick outlives the list it was made from, and one the policy has since
 * dropped would be refused at intake for a choice the page no longer shows.
 * Null sends nothing, so intake applies the default.
 */
export function currentExitCountry(pick: string | null, countries: readonly string[]): string | null {
  return pick !== null && countries.includes(pick) ? pick : null;
}

/** The field's help: what the country decides, then the default it falls to. */
export function exitCountryHelp(lead: string, defaultCountry: string): string {
  if (defaultCountry === '') return lead;
  const fallback = `Default: ${countryName(defaultCountry)}.`;
  return lead === '' ? fallback : `${lead} ${fallback}`;
}

interface ExitCountryProps {
  countries: readonly string[];
  defaultCountry: string;
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
}

/** The country list alone, for a form that labels it itself. */
export function ExitCountrySelect({ countries, defaultCountry, value, onChange, disabled = false }: ExitCountryProps) {
  const picked = currentExitCountry(value, countries);
  const options = useMemo<SelectOption[]>(() => {
    // Annotated: the map's inferred element type makes leading required, and
    // the Automatic entry has none.
    const opts: SelectOption[] = countries
      .map((code) => ({
        value: code,
        label: countryName(code),
        description: code === defaultCountry ? 'Default' : undefined,
        leading: <CountryFlag code={code} />,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'en'));
    // Shown only while a pick stands, and the only way back to the deployment
    // default without a page reload: picking it sends nothing.
    if (picked !== null) {
      opts.unshift({
        value: '',
        label: 'Automatic',
        description: defaultCountry !== '' ? `Default: ${countryName(defaultCountry)}` : 'Deployment default',
      });
    }
    return opts;
  }, [countries, defaultCountry, picked]);
  return (
    <Select
      value={picked ?? defaultCountry}
      onChange={(code) => onChange(code === '' ? null : code)}
      options={options}
      ariaLabel="Exit country"
      disabled={disabled}
    />
  );
}

/** The labelled field: the list and a line on where the traffic leaves from. */
export default function ExitCountryField({ help, ...props }: ExitCountryProps & { help: string }) {
  const text = exitCountryHelp(help, props.defaultCountry);
  return (
    <div>
      <span className="label">Exit country</span>
      <ExitCountrySelect {...props} />
      {text !== '' && <span className="help-text block">{text}</span>}
    </div>
  );
}
